import React from "react";
import { X, Plus, Save } from "lucide-react";
import { SegmentedToggle, HelpNote, AccentButton, ADJUSTMENT_ACCENTS } from "../AdjustmentModalChrome";
import ReusableDropdown from "../../../../../components/ReusableDropdown";

// Mirrors the backend isPreTaxDeduction helper in payrollService.js
const isPreTaxDeductionType = (type) => {
  if (!type) return null; // null = not selected yet
  const t = type.toLowerCase();
  return t === "absence" || t === "sick_leave" || t === "sick_leave_25" || t === "late_arrival";
};

const AddDeductionForm = ({
  t,
  theme,
  formStyles,
  showAddForm,
  setShowAddForm,
  newDeduction,
  setNewDeduction,
  lateCalcLoading,
  fetchLateDeduction,
  handleLateHoursChange,
  handleAddDeduction,
  loading,
  language,
  editingDeductionId,
}) => {
  const isEditing = !!editingDeductionId;
  const { accent, accentDark } = ADJUSTMENT_ACCENTS.deduction;
  return (
    <>
      {/* Add Deduction Form */}
      <div style={formStyles.section}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            marginBottom: showAddForm ? "18px" : 0,
          }}
        >
          <h3 style={formStyles.sectionTitle}>
            {isEditing ? t("editDeduction") || "Edit Deduction" : t("addDeduction")}
          </h3>
          <AccentButton
            variant={showAddForm ? "ghost" : "solid"}
            icon={showAddForm ? X : Plus}
            onClick={() => setShowAddForm(!showAddForm)}
            accent={accent}
            accentDark={accentDark}
            theme={theme}
          >
            {showAddForm ? t("cancel") : (isEditing ? t("editDeduction") || "Edit Deduction" : t("addDeduction"))}
          </AccentButton>
        </div>

        {showAddForm && (
          <div>
            {/* Deduction Treatment Selector & Alert Banner */}
            <div style={{ ...formStyles.formGroup, marginBottom: "20px" }}>
              <label style={formStyles.label}>
                {t("deductionTreatment") || "Deduction Treatment"}
              </label>
              <SegmentedToggle
                accent={accent}
                theme={theme}
                options={[
                  {
                    label: t("net") || "Net",
                    active: !newDeduction.isPreTax,
                    onClick: () => setNewDeduction((prev) => ({ ...prev, isPreTax: false })),
                  },
                  {
                    label: t("gross") || "Gross",
                    active: !!newDeduction.isPreTax,
                    onClick: () => setNewDeduction((prev) => ({ ...prev, isPreTax: true })),
                  },
                ]}
              />
              <HelpNote theme={theme}>
                {newDeduction.isPreTax
                  ? (t("grossDeductionTaxShieldNote") ||
                    "The amount you enter here is the Gross figure. Once saved, the system converts it to the employee's real net-pay impact (usually a smaller number) based on their tax bracket and insurance, so take-home pay drops by less than the amount you type.")
                  : (t("netDeductionDesc") || "This deduction will be taken from the final net amount.")}
              </HelpNote>
            </div>

            <div style={formStyles.formGrid}>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t("deductionType")}</label>
                <ReusableDropdown
                  items={[
                    { id: "absence", label: t("absence") },
                    { id: "penalty", label: t("penalty") },
                    { id: "advance", label: t("advance") },
                    { id: "insurance", label: t("insurance") },
                    { id: "sick_leave_25", label: t("sickLeave") },
                    { id: "late_arrival", label: t("lateArrival") },
                    // ── The rest of the types the bulk-import template and
                    // backend already recognize (see exportAllowanceDeductionTemplate
                    // in payrollController.js) — added so every type that can
                    // be imported can also be added here one at a time.
                    { id: "late", label: t("late") || "Late" },
                    { id: "unpaid_leave", label: t("unpaidLeave") || "Unpaid Leave" },
                    { id: "aman_card", label: t("amanCard") || "Aman Card" },
                    { id: "amen_gebak", label: t("amenGebak") || "Amen Gebak" },
                    { id: "premium_card", label: t("premiumCard") || "Premium Card" },
                    { id: "computer_installment", label: t("computerInstallment") || "Computer Installment" },
                    { id: "raya_installment", label: t("rayaInstallment") || "Raya Installment" },
                    { id: "salary_advance", label: t("salaryAdvance") || "Salary Advance" },
                    { id: "emergency_fund", label: t("emergencyFund") || "Emergency Fund" },
                    { id: "medical_insurance", label: t("medicalInsurance") || "Medical Insurance" },
                    { id: "adjustment", label: t("adjustment") || "Adjustment" },
                    { id: "other", label: t("other") },
                  ]}
                  value={newDeduction.type || ""}
                  onChange={(val) => {
                    if (val) {
                      setNewDeduction({
                        ...newDeduction,
                        type: val,
                      });
                      if (val === "late_arrival") {
                        fetchLateDeduction();
                      }
                    }
                  }}
                  onSelect={(item) => {
                    setNewDeduction({ ...newDeduction, type: item.id });
                    if (item.id === "late_arrival") {
                      fetchLateDeduction();
                    }
                  }}
                  placeholder={t("selectType")}
                  theme={theme}
                  language={language}
                  maxItems={30}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  showAllOnArrowClick={true}
                  showClear={true}
                  onClear={() =>
                    setNewDeduction({
                      ...newDeduction,
                      type: "",
                      amount: "",
                      description: "",
                    })
                  }
                  inputStyle={formStyles.input}
                />
              </div>

              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t("amount")}</label>
                {lateCalcLoading && newDeduction.type === "late_arrival" ? (
                  <div
                    style={{
                      ...formStyles.input,
                      display: "flex",
                      alignItems: "center",
                      color: theme === "dark" ? "#aaa" : "#888",
                    }}
                  >
                    {t("calculatingLate")}
                  </div>
                ) : (
                  <input
                    type="number"
                    style={{
                      ...formStyles.input,
                      ...(newDeduction.type === "late_arrival"
                        ? {
                          backgroundColor: theme === "dark" ? "#333" : "#f0f0f0",
                          cursor: "not-allowed",
                        }
                        : {}),
                    }}
                    value={newDeduction.amount}
                    onChange={(e) => {
                      if (newDeduction.type !== "late_arrival") {
                        setNewDeduction({
                          ...newDeduction,
                          amount: e.target.value,
                        });
                      }
                    }}
                    readOnly={newDeduction.type === "late_arrival"}
                    placeholder="0.00"
                  />
                )}
              </div>

              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>
                  {newDeduction.type === "late_arrival"
                    ? t("lateHours")
                    : t("days")}
                </label>
                <input
                  type="number"
                  style={formStyles.input}
                  value={newDeduction.days}
                  onChange={(e) => {
                    if (newDeduction.type === "late_arrival") {
                      handleLateHoursChange(e.target.value);
                    } else {
                      setNewDeduction({
                        ...newDeduction,
                        days: e.target.value,
                      });
                    }
                  }}
                  placeholder={newDeduction.type === "late_arrival" ? "0" : "0"}
                  step={newDeduction.type === "late_arrival" ? "0.5" : "1"}
                />
              </div>
            </div>



            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t("description")}</label>
              <textarea
                style={{
                  ...formStyles.input,
                  minHeight: "80px",
                  resize: "vertical",
                }}
                value={newDeduction.description}
                onChange={(e) =>
                  setNewDeduction({
                    ...newDeduction,
                    description: e.target.value,
                  })
                }
                placeholder={t("descriptionPlaceholder")}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <AccentButton
                icon={Save}
                onClick={handleAddDeduction}
                disabled={loading}
                accent={accent}
                accentDark={accentDark}
                theme={theme}
              >
                {loading ? t("saving") : (isEditing ? t("update") || "Update" : t("saveDeduction"))}
              </AccentButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AddDeductionForm;